import { Injectable } from '@angular/core';
import {
  AbstractControlOptions,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  buildInitialOwnerShares,
  OwnerShare,
} from '@app/components/pr-form/pr-input-owner-share/pr-input-owner-share.component';
import { UtilsService } from '@app/providers/utils/utils.service';
import { EaEstateLoanFormService } from '../ea-estate-loan-form/ea-estate-loan-form.service';
import { EaEstimationFormService } from '../ea-estimation-form/ea-estimation-form.service';
import { EaFeesFormService } from '../ea-fees-form/ea-fees-form.service';
import { EaRealestateFormService } from '../ea-realestate-form/ea-realestate-form.service';
import { EaRentFormPersonalUseService } from '../ea-rent-form-personal-use/ea-rent-form-personal-use.service';

@Injectable({
  providedIn: 'root',
})
export class EaEstateFormService {
  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected utilsService: UtilsService,
    protected eaEstimationFormService: EaEstimationFormService,
    protected eaRealestateFormService: EaRealestateFormService,
    protected eaEstateLoanFormService: EaEstateLoanFormService,
    protected eaFeesFormService: EaFeesFormService,
    protected eaRentFormPersonalUseService: EaRentFormPersonalUseService,
  ) {}

  public generate(entity: any) {
    let options: AbstractControlOptions = {};
    if (!entity) entity = {};
    let mainLoan: any = undefined;
    if (entity && entity.loans && entity.loans[0]) mainLoan = entity.loans[0];
    let mainAddress: any = {};
    if (entity.property) mainAddress = entity.property;
    return this.formBuilder.group(
      {
        id: [entity ? entity.id : null, Validators.nullValidator],
        personalUseType: [
          entity ? entity.personalUseType : null,
          Validators.required,
        ],
        userId: [entity ? entity.userId : null, Validators.nullValidator],
        accountId: [entity ? entity.accountId : null, Validators.nullValidator],
        typeUrl: [entity ? entity.typeUrl : null, Validators.nullValidator],
        ownershipType: [
          entity ? entity.ownershipType : null,
          Validators.required,
        ],
        loans: this.eaEstateLoanFormService.generate(
          mainLoan || { accountId: entity.accountId },
        ),
        fees: this.eaFeesFormService.generate(entity),
        rents: this.eaRentFormPersonalUseService.generate(entity),
        estimations: this.eaEstimationFormService.generate(entity),
        purchasePrice: [
          entity ? entity.purchasePrice : null,
          Validators.required,
        ],
        label: [entity ? entity.label : null, Validators.required],
        startDate: [entity ? entity.startDate : null, Validators.required],
        subscriptionSource: [
          entity ? entity.subscriptionSource : null,
          Validators.required,
        ],
        subscriptionSourceDetail: [
          entity ? entity.subscriptionSourceDetail : null,
          Validators.nullValidator,
        ],
        // Détenteurs + part de détention (`OwnerShare[]`) pour
        // `<pr-input-owner-share>`. `share` repris s'il existe, sinon 1/n.
        owners: [buildInitialOwnerShares(entity), Validators.required],
        areaSize: [entity ? mainAddress.areaSize : null, Validators.required],
        residenceType: [
          entity && mainAddress?.type ? mainAddress.type : 'APPARTMENT',
          Validators.required,
        ],
        address: this.formBuilder.group({
          street: [
            mainAddress ? mainAddress.street : null,
            Validators.required,
          ],
          zipCode: [
            mainAddress ? mainAddress.zipCode : 0,
            Validators.nullValidator,
          ],
          city: [mainAddress ? mainAddress.city : null, Validators.required],
          country: [
            mainAddress ? mainAddress.country : null,
            Validators.required,
          ],
        }),
      },
      options,
    );
  }

  public extract(form: UntypedFormGroup, nestedForms?) {
    let dto = {
      typeUrl: this.utilsService.emptyIsNull(form.value.typeUrl),
      id: this.utilsService.emptyIsNull(form.value.id),
      userId: this.utilsService.emptyIsNull(form.value.userId),
      personalUseType: this.utilsService.emptyIsNull(
        form.value.personalUseType,
      ),
      ownershipType: this.utilsService.emptyIsNull(form.value.ownershipType),
      currentOwnershipType: this.utilsService.emptyIsNull(
        form.value.ownershipType,
      ),
      expenses: form.get('fees').value.fees,
      rents: this.eaRentFormPersonalUseService.extract(form.get('rents') as UntypedFormGroup).rents,
      estimations: form.get('estimations').value.array,
      loans: this.eaEstateLoanFormService.extract(
        form.get('loans') as UntypedFormGroup,
      ),
      label: this.utilsService.emptyIsNull(form.value.label),
      purchasePrice: this.utilsService.emptyIsNull(form.value.purchasePrice),
      startDate: this.utilsService.toIsoDate(form.value.startDate),
      subscriptionSource: this.utilsService.emptyIsNull(
        form.value.subscriptionSource,
      ),
      subscriptionSourceDetail: this.utilsService.emptyIsNull(
        form.value.subscriptionSourceDetail,
      ),
      property: {
        type: this.utilsService.emptyIsNull(form.value.residenceType),
        areaSize: this.utilsService.emptyIsNull(form.value.areaSize),
        street: this.utilsService.emptyIsNull(
          form.controls.address.get('street').value,
        ),
        zipCode: this.utilsService.emptyIsNull(
          form.controls.address.get('zipCode').value,
        ),
        city: this.utilsService.emptyIsNull(
          form.controls.address.get('city').value,
        ),
        country: this.utilsService.emptyIsNull(
          form.controls.address.get('country').value.value,
        ),
      },
      // Détenteurs + part de détention (`share`, fraction 0..1) saisis via
      // `<pr-input-owner-share>` : la valeur est déjà un `OwnerShare[]`.
      owners: ((form.value.owners || []) as OwnerShare[])
        .filter(o => o?.ownerId != null)
        .map(o => ({ ownerId: Number(o.ownerId), share: o.share })),
    };
    console.debug(dto);
    return dto;
  }
}
